/** biome-ignore-all lint/suspicious/noConsole: don't need logger here */
import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'
import LogRocket from 'logrocket'
import { isDev, isProd } from 'v2/configs/constants'
import { isInternalSession } from 'v2/utils/internal-user'

enum LogLevel {
  LOG = 'LOG',
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  CRITICAL = 'CRITICAL',
}

interface LoggerTags {
  orgName: string
  aomId: string
  roles: string
  email: string
  [key: string]: string
}

const tags: LoggerTags = {
  orgName: '',
  aomId: '',
  roles: '',
  email: '',
  name: '',
  region: '',
}

export const setLoggerTags = (loggerTags: Partial<LoggerTags>) => {
  try {
    Object.assign(tags, loggerTags)
    // Set tags in Sentry for better error tracking
    Sentry.setUser({ email: tags.email, id: tags.aomId })
    Object.entries(loggerTags).forEach(([key, value]) => {
      Sentry.setTag(key, value)
    })

    // Skipped for internal sessions: initLogRocket never ran for them, and the SDK
    // buffers pre-init calls until it hits 1000 and warns about a network fault.
    if (!isInternalSession()) {
      LogRocket.identify(tags.aomId, {
        email: tags.email,
        roles: tags.roles,
        orgName: tags.orgName,
      })
    }
  } catch (error) {
    console.error('Error setting logger tags', error)
  }
}

const getTags = () => tags

const formatLogMessage = (type: string, message: string) => {
  const timestamp = dayjs().format('D/MMM HH:mm:ss.SSS A')
  return `${timestamp} - CX: [${type}] ${message}`
}

const createLogMethod =
  (
    method: keyof Pick<Console, 'log' | 'error' | 'warn' | 'info' | 'debug'>,
    level: LogLevel,
    type: string,
    isLocalLog = false,
  ) =>
  (message: string, ...args: unknown[]) => {
    const formattedMessage = formatLogMessage(type, message)

    if (isProd) {
      // If in production, log to LogRocket except for local logs
      // Logrocket also captures local console logs by default
      if (isLocalLog) console.log(formattedMessage, ...args)
      else if (LogRocket?.[method] && !isInternalSession())
        LogRocket[method](formattedMessage, ...args)
    } else {
      console[method](formattedMessage, ...args)
    }

    // Sentry on both dev and prod
    if (isProd || isDev) {
      let stringifiedArgs = []
      try {
        stringifiedArgs = args.map((arg) => JSON.stringify(arg))
      } catch {
        stringifiedArgs = args
      }
      const sentryExtras = { tags: getTags(), extra: { args: stringifiedArgs, formattedMessage } }

      const captureSentryEvent = (severityLevel: Sentry.SeverityLevel) => {
        // Find the first Error instance in args (can be at any position)
        const errorArg = args.find(
          (arg) => arg instanceof Error || (arg && typeof arg === 'object' && 'stack' in arg),
        )

        Sentry.withScope((scope) => {
          scope.setLevel(severityLevel)
          scope.setTags(sentryExtras.tags)
          scope.setExtras(sentryExtras.extra)

          if (errorArg) Sentry.captureException(errorArg)
          else Sentry.captureMessage(message, severityLevel)
        })
      }

      if (level === LogLevel.CRITICAL) captureSentryEvent('fatal')
      else if (level === LogLevel.ERROR) captureSentryEvent('error')
    }
  }

interface Logger {
  log: (message: string, ...args: unknown[]) => void
  error: (message: string, ...args: unknown[]) => void
  warn: (message: string, ...args: unknown[]) => void
  info: (message: string, ...args: unknown[]) => void
  debug: (message: string, ...args: unknown[]) => void
  /**
   * For local logging in production
   */
  local: (message: string, ...args: unknown[]) => void
  /**
   * For critical errors that should be logged and reported to Sentry
   */
  critical: (message: string, ...args: unknown[]) => void
}

export const createLogger = (type: string): Logger => ({
  log: createLogMethod('log', LogLevel.LOG, type),
  error: createLogMethod('error', LogLevel.ERROR, type),
  warn: createLogMethod('warn', LogLevel.WARN, type),
  info: createLogMethod('info', LogLevel.INFO, type),
  debug: createLogMethod('debug', LogLevel.DEBUG, type),
  local: createLogMethod('log', LogLevel.LOG, type, true),
  critical: createLogMethod('error', LogLevel.CRITICAL, type),
})

// For backwards compatibility
export const logger = createLogger('App')
export const pubnubLogger = createLogger('PubNub')
export const phoneLogger = createLogger('Phone SDK')
export const tabLogger = createLogger('Tab Manager')
export const agentLogger = createLogger('Agent')
export const vibeAgentLogger = createLogger('VibeAgent')
